import React, { useState } from 'react';
import { Download, Send, Search, FileText, Clock, Users, Inbox } from 'lucide-react';
import useGroupProjectManagementStore from '@/stores/TrainerStore/GroupProjectManagementStore';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';
import './SubmissionsGrading.css';

const SubmissionGradingUI: React.FC = () => {
  const { submissions, loading, error } = useGroupProjectManagementStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('Select Team');
  const [selectedStatus, setSelectedStatus] = useState('Submitted Status');
  const [grade, setGrade] = useState('');
  const [feedback, setFeedback] = useState('');

  const submission = submissions?.[0];

  const handleSubmitGrade = () => {
    if (grade && feedback) {
      alert(`Grade: ${grade}%\nFeedback: ${feedback}`);
    }
  };

  // Loading state
  if (loading && !submissions) {
    return (
      <div className="trainer-group-submission-grading-container">
        <LoadingSpinner size="lg" message="Loading submissions..." fullScreen={false} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="trainer-group-submission-grading-container">
        <ErrorDisplay
          title="Failed to Load Submissions"
          message={error}
          showRetry={false}
          type="error"
        />
      </div>
    );
  }

  // Empty state
  if (!submission) {
    return (
      <div className="trainer-group-submission-grading-container">
        <div className="trainer-group-submission-grading-filters">
          <div className="trainer-group-submission-grading-search">
            <Search className="trainer-group-submission-grading-search-icon" size={20} />
            <input
              type="text"
              placeholder="Search & Select project"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="trainer-group-submission-grading-search-input"
            />
          </div>

          <div className="trainer-group-submission-grading-dropdown">
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="trainer-group-submission-grading-select"
            >
              <option>Select Team</option>
              <option>Design Squad</option>
              <option>Dev Team</option>
              <option>Marketing Team</option>
            </select>
          </div>

          <div className="trainer-group-submission-grading-dropdown">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="trainer-group-submission-grading-select"
            >
              <option>Submitted Status</option>
              <option>Submitted</option>
              <option>Graded</option>
              <option>Pending</option>
            </select>
          </div>
        </div>

        <div className="trainer-group-empty-state">
          <div className="trainer-group-empty-state-content">
            <div className="trainer-group-empty-state-icon">
              <Inbox size={64} />
            </div>
            <h3 className="trainer-group-empty-state-title">No submissions yet</h3>
            <p className="trainer-group-empty-state-message">
              Select a project from the search above to view and grade team submissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trainer-group-submission-grading-container">
      <div className="trainer-group-submission-grading-filters">
        <div className="trainer-group-submission-grading-search">
          <Search className="trainer-group-submission-grading-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search & Select project"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="trainer-group-submission-grading-search-input"
          />
        </div>

        <div className="trainer-group-submission-grading-dropdown">
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="trainer-group-submission-grading-select"
          >
            <option>Select Team</option>
            <option>Design Squad</option>
            <option>Dev Team</option>
            <option>Marketing Team</option>
          </select>
        </div>

        <div className="trainer-group-submission-grading-dropdown">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="trainer-group-submission-grading-select"
          >
            <option>Submitted Status</option>
            <option>Submitted</option>
            <option>Graded</option>
            <option>Pending</option>
          </select>
        </div>
      </div>

      <div className="trainer-group-submission-grading-card">
        <div className="trainer-group-submission-grading-header">
          <div className="trainer-group-submission-grading-title-section">
            <h2 className="trainer-group-submission-grading-title">{submission.submissionTitle}</h2>
            <span className="trainer-group-submission-grading-badge">{submission.status}</span>
          </div>

          <div className="trainer-group-submission-grading-members">
            <Users size={18} className="trainer-group-submission-grading-members-icon" />
            <span className="trainer-group-submission-grading-members-text">
              Team: {submission.teamName}
            </span>
          </div>
        </div>

        <div className="trainer-group-submission-grading-files-section">
          <h3 className="trainer-group-submission-grading-files-title">Submitted Files</h3>

          {submission.submissionFileUrl && submission.submissionFileName && (
            <div className="trainer-group-submission-grading-file-item">
              <div className="trainer-group-submission-grading-file-info">
                <FileText size={18} className="trainer-group-submission-grading-file-icon" />
                <span className="trainer-group-submission-grading-file-name">{submission.submissionFileName}</span>
              </div>
              <button className="trainer-group-submission-grading-download-btn">
                <Download size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="trainer-group-submission-grading-date">
          <Clock size={16} className="trainer-group-submission-grading-date-icon" />
          <span>Submitted on {submission.submittedDate}</span>
        </div>

        <div className="trainer-group-submission-grading-form">
          <div className="trainer-group-submission-grading-input-group">
            <label className="trainer-group-submission-grading-label">Grade (%)</label>
            <input
              type="number"
              placeholder="Enter grade"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="trainer-group-submission-grading-input"
              min="0"
              max="100"
            />
          </div>

          <div className="trainer-group-submission-grading-input-group">
            <label className="trainer-group-submission-grading-label">Feedback</label>
            <textarea
              placeholder="Provide feedback to the team..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="trainer-group-submission-grading-textarea"
              rows={4}
            />
          </div>

          <button
            onClick={handleSubmitGrade}
            className="trainer-group-submission-grading-submit-btn"
          >
            <Send size={18} />
            Submit Grade & Feedback
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionGradingUI;
